import PropTypes from "prop-types"
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react"
import { createPortal } from "react-dom"
import { Collapse } from "reactstrap"
import { Link, useLocation } from "react-router-dom"
import withRouter from "components/Common/withRouter"

//i18n
import { withTranslation } from "react-i18next"

import { connect } from "react-redux"
import usePermissionRefresh from "hooks/usePermissionRefresh"
import {
  useAssignedBrands,
  canViewBrandOrders,
  getBrandOrdersBasePath,
} from "hooks/useAssignedBrands"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { hasPlanSocialLoginAccess } from "utils/planSocialLogins"

let horizontalNavScrollLeft = 0

const DROPDOWN_PARENT_ROUTE_RULES = [
  { id: "menus", test: path => path.startsWith("/menus") },
  {
    id: "customers",
    test: path => path === "/customers" || path.startsWith("/customers/"),
  },
  { id: "customer-support", test: path => path.startsWith("/support-") },
  {
    id: "countries",
    test: path =>
      ["/countries", "/regions", "/cities", "/zones"].some(
        base => path === base || path.startsWith(`${base}/`)
      ),
  },
  {
    id: "reports",
    test: path =>
      [
        "/sales-reports",
        "/wallet-reports",
        "/payments-reports",
        "/business-tax-reports",
        "/loyalty-points-reports",
      ].some(base => path === base || path.startsWith(`${base}/`)),
  },
  {
    id: "ratings",
    test: path =>
      ["/rating-questions", "/branch-ratings"].some(
        base => path === base || path.startsWith(`${base}/`)
      ),
  },
  {
    id: "gift-card",
    test: path =>
      ["/eidiyah", "/dynamic-link", "/eidiyah-usage"].some(
        base => path === base || path.startsWith(`${base}/`)
      ),
  },
  {
    id: "discounts",
    test: path =>
      [
        "/discounts",
        "/coupons-group",
        "/coupons",
        "/coupon-usage",
        "/wallet-coupons",
      ].some(base => path === base || path.startsWith(`${base}/`)),
  },
  {
    id: "notifications",
    test: path =>
      ["/app-notifications", "/push-notifications"].some(
        base => path === base || path.startsWith(`${base}/`)
      ),
  },
  {
    id: "settings",
    test: path =>
      [
        "/my-preference",
        "/app-settings",
        "/eidiyah-settings",
        "/add-emails",
        "/referral-settings",
        "/loyalty-settings",
        "/order-cancel-settings",
        "/ingredients",
        "/allergens",
        "/static-maintenance",
      ].some(base => path === base || path.startsWith(`${base}/`)),
  },
  {
    id: "orders",
    test: path => path === "/orders" || path.startsWith("/orders/"),
  },
  {
    id: "pos-settings",
    test: path =>
      ["/api-key", "/branch-sync", "/menu-sync"].some(
        base => path === base || path.startsWith(`${base}/`)
      ),
  },
]

const resolveDropdownParentId = pathname => {
  const rule = DROPDOWN_PARENT_ROUTE_RULES.find(({ test }) => test(pathname))
  return rule?.id || null
}

const HorizontalNavDropdownContext = createContext(null)

const HorizontalDropdownMenu = ({ id, children }) => {
  const ctx = useContext(HorizontalNavDropdownContext)

  if (!ctx || ctx.openDropdown !== id || !ctx.portalContainer) {
    return null
  }

  return createPortal(
    <div
      className="dropdown-menu horizontal-dropdown-menu show"
      style={ctx.getDropdownStyle(id)}
      data-horizontal-dropdown-menu={id}
    >
      {children}
    </div>,
    ctx.portalContainer
  )
}

HorizontalDropdownMenu.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string.isRequired,
}

const Navbar = props => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [isDesktopNav, setIsDesktopNav] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 992
  )

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopNav(window.innerWidth >= 992)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const [openDropdown, setOpenDropdown] = useState(null)
  const [dropdownPosition, setDropdownPosition] = useState(null)
  const [dropdownPortalEl, setDropdownPortalEl] = useState(null)
  const navScrollRef = useRef(null)
  const ignoreOutsideClickRef = useRef(false)

  const persistNavScroll = useCallback(() => {
    if (navScrollRef.current) {
      horizontalNavScrollLeft = navScrollRef.current.scrollLeft
    }
  }, [])

  const restoreNavScroll = useCallback(() => {
    if (navScrollRef.current) {
      navScrollRef.current.scrollLeft = horizontalNavScrollLeft
    }
  }, [])

  useEffect(() => {
    const container = navScrollRef.current
    if (!container) return undefined

    container.addEventListener("scroll", persistNavScroll, { passive: true })
    restoreNavScroll()

    return () => {
      container.removeEventListener("scroll", persistNavScroll)
      persistNavScroll()
    }
  }, [persistNavScroll, restoreNavScroll])

  useLayoutEffect(() => {
    restoreNavScroll()
    requestAnimationFrame(restoreNavScroll)
  }, [location.pathname, restoreNavScroll])

  const closeDropdown = useCallback(() => {
    setOpenDropdown(null)
    setDropdownPosition(null)
  }, [])

  const updateDropdownPosition = (id, trigger) => {
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    setDropdownPosition({
      top: rect.bottom,
      left: rect.left,
      minWidth: Math.max(rect.width, 192),
    })
  }

  const toggleDropdown = (id, event) => {
    event.preventDefault()
    event.stopPropagation()
    ignoreOutsideClickRef.current = true
    window.setTimeout(() => {
      ignoreOutsideClickRef.current = false
    }, 0)
    if (openDropdown === id) {
      setOpenDropdown(null)
      setDropdownPosition(null)
      return
    }
    setOpenDropdown(id)
    updateDropdownPosition(id, event.currentTarget)
  }

  const getDropdownStyle = useCallback(
    id => {
      if (openDropdown !== id || !dropdownPosition) return undefined
      return {
        "--horizontal-dropdown-top": `${dropdownPosition.top}px`,
        "--horizontal-dropdown-left": `${dropdownPosition.left}px`,
        "--horizontal-dropdown-min-width": `${dropdownPosition.minWidth}px`,
      }
    },
    [openDropdown, dropdownPosition]
  )

  const dropdownContextValue = useMemo(
    () => ({
      openDropdown,
      portalContainer: dropdownPortalEl,
      getDropdownStyle,
    }),
    [openDropdown, dropdownPortalEl, getDropdownStyle]
  )

  useEffect(() => {
    if (!openDropdown) return undefined

    const syncPosition = () => {
      const trigger = document.querySelector(
        `[data-horizontal-dropdown="${openDropdown}"]`
      )
      updateDropdownPosition(openDropdown, trigger)
    }

    window.addEventListener("resize", syncPosition)
    window.addEventListener("scroll", syncPosition, true)

    const scrollContainer = navScrollRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", syncPosition, { passive: true })
    }

    return () => {
      window.removeEventListener("resize", syncPosition)
      window.removeEventListener("scroll", syncPosition, true)
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", syncPosition)
      }
    }
  }, [openDropdown])

  useEffect(() => {
    const handleOutsideClick = event => {
      if (ignoreOutsideClickRef.current) return
      if (
        event.target.closest("[data-horizontal-dropdown]") ||
        event.target.closest(".horizontal-dropdown-menu")
      ) {
        return
      }
      closeDropdown()
    }

    document.addEventListener("click", handleOutsideClick)
    return () => document.removeEventListener("click", handleOutsideClick)
  }, [closeDropdown])

  useEffect(() => {
    persistNavScroll()
    closeDropdown()
  }, [location.pathname, closeDropdown, persistNavScroll])

  const normalizedPath = useMemo(
    () => (location.pathname || "").replace(/\/+$/, "") || "/",
    [location.pathname]
  )

  const { isLoggedIn, userRole, accountType, planSocialLogins } = useSelector(
    state => state.Login
  )

  usePermissionRefresh()

  const isSocialAuthSidebarVisible = useMemo(
    () =>
      isPermissionEnabled(userRole, 53, "social_auth_settings_1", accountType) &&
      hasPlanSocialLoginAccess(planSocialLogins),
    [userRole, accountType, planSocialLogins]
  )

  const [userSideBarPermissions, setUserSideBarPermissions] = useState({
    isDashboardEidiyahView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_5",
      accountType
    ),
    isDashboardSalesView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_1",
      accountType
    ),
    isDashboardBranchesView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_2",
      accountType
    ),
    isDashboardBookingsView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_3",
      accountType
    ),
    isDashboardRatingsView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_4",
      accountType
    ),
    isDashboardEstoreView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_6",
      accountType
    ),
    isDashboardMarketingView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_7",
      accountType
    ),
    isDashboardWalletView: isPermissionEnabled(
      userRole,
      1,
      "dashboard_8",
      accountType
    ),

    isInAppContentView: isPermissionEnabled(
      userRole,
      2,
      "inAppContent_1",
      accountType
    ),
    isOfferBannerView: isPermissionEnabled(
      userRole,
      49,
      "offerBanner_1",
      accountType
    ),
    isItemTagView: isPermissionEnabled(
      userRole,
      4,
      "item_tag_1",
      accountType
    ),
    isRolesView: isPermissionEnabled(userRole, 3, "roles_1", accountType),
    isMenuView: isPermissionEnabled(userRole, 4, "menus_1", accountType),
    isLocationsView: isPermissionEnabled(
      userRole,
      5,
      "branchLocations_1",
      accountType
    ),
    isUsersView: isPermissionEnabled(
      userRole,
      6,
      "usersPermission_1",
      accountType
    ),
    isCustomersView: isPermissionEnabled(
      userRole,
      7,
      "customersPermission_1",
      accountType
    ),
    customerSubPermissions: getSubPermissions(
      userRole,
      7,
      "customersPermission_1",
      accountType
    ),
    isCustomersBlacklistView: isPermissionEnabled(
      userRole,
      7,
      "customersPermission_3",
      accountType
    ),
    isFeedbackView: isPermissionEnabled(userRole, 8, "feedback_1", accountType),
    isOrdersView: isPermissionEnabled(userRole, 9, "order_1", accountType),
    isCurbsidePickupView: isPermissionEnabled(
      userRole,
      10,
      "curbside_1",
      accountType
    ),
    isMenusView: isPermissionEnabled(userRole, 4, "menus_1", accountType),
    MenusSubPermissions: getSubPermissions(userRole, 4, "menus_1", accountType),
    MenusUpdateSubPermissions: getSubPermissions(
      userRole,
      4,
      "menus_3",
      accountType
    ),
    isCountriesView: isPermissionEnabled(
      userRole,
      11,
      "country_1",
      accountType
    ),
    isTaxView: isPermissionEnabled(userRole, 12, "tax_1", accountType),
    isApiLogsView: isPermissionEnabled(userRole, 13, "api_logs_1", accountType),
    isPromotionView: isPermissionEnabled(
      userRole,
      14,
      "promotion_1",
      accountType
    ),
    isCouponRedeemView: isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_1",
      accountType
    ),
    isNotificationView: isPermissionEnabled(
      userRole,
      15,
      "notification_1",
      accountType
    ),
    isReportView: isPermissionEnabled(userRole, 16, "report_1", accountType),
    isEstoreReportView: isPermissionEnabled(
      userRole,
      45,
      "estore_report_1",
      accountType
    ),
    isCustomerSupportView: isPermissionEnabled(
      userRole,
      17,
      "customer_support_1",
      accountType
    ),
    isFaqView: isPermissionEnabled(userRole, 18, "faq_1", accountType),
    isReferralView: isPermissionEnabled(
      userRole,
      19,
      "referral_1",
      accountType
    ),
    isLoyaltyView: isPermissionEnabled(userRole, 20, "loyalty_1", accountType),
    isBookingView: isPermissionEnabled(userRole, 21, "booking_1", accountType),
    isQrCodeView: isPermissionEnabled(userRole, 22, "qr_code_1", accountType),
    isIngredientView: isPermissionEnabled(
      userRole,
      23,
      "ingredient_1",
      accountType
    ),
    isAllergenView: isPermissionEnabled(
      userRole,
      24,
      "allergen_1",
      accountType
    ),
    isRegionView: isPermissionEnabled(userRole, 25, "region_1", accountType),
    isCityView: isPermissionEnabled(userRole, 26, "city_1", accountType),
    isZoneView: isPermissionEnabled(userRole, 43, "zone_1", accountType),
    isRatingView: isPermissionEnabled(userRole, 27, "rating_1", accountType),
    isAPIKeyView: isPermissionEnabled(userRole, 28, "api_key_1", accountType),
    isBranchSyncView: isPermissionEnabled(
      userRole,
      29,
      "branch_sync_1",
      accountType
    ),
    isMenuSyncView: isPermissionEnabled(
      userRole,
      30,
      "menu_sync_3",
      accountType
    ),
    isAddEmailView: isPermissionEnabled(
      userRole,
      31,
      "add_emails_1",
      accountType
    ),
    isAppSettingsView: isPermissionEnabled(
      userRole,
      32,
      "app_settings_1",
      accountType
    ),
    AppSettingsViewSubPermissions: getSubPermissions(
      userRole,
      32,
      "app_settings_1",
      accountType
    ),
    AppSettingsUpdateSubPermissions: getSubPermissions(
      userRole,
      32,
      "app_settings_3",
      accountType
    ),
    isOrderCancelSettingsView: isPermissionEnabled(
      userRole,
      50,
      "order_cancel_settings_1",
      accountType
    ),
    isTermsConditionsView: isPermissionEnabled(
      userRole,
      33,
      "term_condition_1",
      accountType
    ),
    isPrivacyPolicyView: isPermissionEnabled(
      userRole,
      34,
      "privacy_policy_1",
      accountType
    ),
    isVehicleView: isPermissionEnabled(userRole, 35, "vehicle_1", accountType),
    isBrandsManagementView: isPermissionEnabled(
      userRole,
      52,
      "brands_1",
      accountType
    ),
    isDeliveryPartnersView: isPermissionEnabled(
      userRole,
      47,
      "delivery_partners_1",
      accountType
    ),
    isDeliveryPartnerSettingsView: isPermissionEnabled(
      userRole,
      47,
      "delivery_partners_6",
      accountType
    ),
    isIntegrationsView: isPermissionEnabled(
      userRole,
      48,
      "tenant_integrations_1",
      accountType
    ),
    isSocialAuthSettingsView:
      isPermissionEnabled(userRole, 53, "social_auth_settings_1", accountType),
    isBillingView: isPermissionEnabled(
      userRole,
      51,
      "billing_1",
      accountType
    ),
    isUserActivityView: isPermissionEnabled(
      userRole,
      36,
      "user_activity_1",
      accountType
    ),
    isGiftCardView: isPermissionEnabled(
      userRole,
      37,
      "gift_card_1",
      accountType
    ),
    isGiftCardUsageView: isPermissionEnabled(
      userRole,
      38,
      "gift_card_usage_1",
      accountType
    ),
    isDynamicLinkView: isPermissionEnabled(
      userRole,
      39,
      "dynamic_link_1",
      accountType
    ),
    isEidiyahSettingsView: isPermissionEnabled(
      userRole,
      40,
      "gift_card_settings_1",
      accountType
    ),
    isStaticMaintenanceView: isPermissionEnabled(
      userRole,
      41,
      "static_maintenance_1",
      accountType
    ),
  })

  const { brands: assignedBrands } = useAssignedBrands()

  const visibleOrderBrands = useMemo(
    () =>
      assignedBrands.filter(brand =>
        canViewBrandOrders(brand, userSideBarPermissions.isOrdersView)
      ),
    [assignedBrands, userSideBarPermissions.isOrdersView]
  )

  const ordersTargetPath = useMemo(() => {
    if (visibleOrderBrands.length === 1) {
      return getBrandOrdersBasePath(visibleOrderBrands[0].brandId)
    }
    return "/orders"
  }, [visibleOrderBrands])

  const ordersUseDropdown = visibleOrderBrands.length >= 2

  const activeDropdownId = useMemo(() => {
    const dropdownId = resolveDropdownParentId(normalizedPath)
    if (dropdownId === "orders" && !ordersUseDropdown) {
      return null
    }
    return dropdownId
  }, [normalizedPath, ordersUseDropdown])

  const isDirectNavActive = useCallback(
    path => {
      const normalized = (path || "").replace(/\/+$/, "") || "/"
      const isMatch =
        normalizedPath === normalized ||
        (normalized !== "/" && normalizedPath.startsWith(`${normalized}/`))

      return isMatch && !activeDropdownId
    },
    [normalizedPath, activeDropdownId]
  )

  const getDropdownNavItemClass = useCallback(
    id =>
      `nav-item dropdown${openDropdown === id ? " show" : ""}${
        activeDropdownId === id ? " active" : ""
      }`,
    [openDropdown, activeDropdownId]
  )

  const getDropdownTriggerClass = useCallback(
    id =>
      `nav-link dropdown-toggle arrow-none${
        activeDropdownId === id ? " active" : ""
      }`,
    [activeDropdownId]
  )

  const getDirectNavItemClass = useCallback(
    path => `nav-item dropdown${isDirectNavActive(path) ? " active" : ""}`,
    [isDirectNavActive]
  )

  const getDirectNavLinkClass = useCallback(
    path =>
      `nav-link dropdown-toggle arrow-none${
        isDirectNavActive(path) ? " active" : ""
      }`,
    [isDirectNavActive]
  )

  return (
    <React.Fragment>
      <div className="topnav">
        <div
          className="container-fluid"
          // style={{ overflowX: "auto" }}
        >
          <nav
            className="navbar navbar-light navbar-expand-lg topnav-menu horizontal_navbar"
            id="navigation"
          >
            <HorizontalNavDropdownContext.Provider value={dropdownContextValue}>
              <Collapse
                navbar
                isOpen={isDesktopNav || props.leftMenu}
                className="navbar-collapse w-100"
                id="topnav-menu-content"
              >
                <div
                  ref={navScrollRef}
                  className="hover-list-container horizontal-nav-scroll"
                >
                  <ul className="navbar-nav horizontal-navbar-nav">
                  {(userSideBarPermissions.isDashboardEidiyahView ||
                    userSideBarPermissions.isDashboardSalesView ||
                    userSideBarPermissions.isDashboardBranchesView ||
                    userSideBarPermissions.isDashboardRatingsView ||
                    userSideBarPermissions.isDashboardBookingsView ||
                    userSideBarPermissions.isDashboardEstoreView ||
                    userSideBarPermissions.isDashboardMarketingView ||
                    userSideBarPermissions.isDashboardWalletView) ? (
                    <li className={getDirectNavItemClass("/dashboard")}>
                      <Link
                        className={getDirectNavLinkClass("/dashboard")}
                        to="/dashboard"
                      >
                        <i className="bx bx-home-circle me-2"></i>
                        {props.t("Dashboard")}
                      </Link>
                    </li>
                  ) : null}

                  <>
                  {userSideBarPermissions.isInAppContentView ? (
                    <li className={getDirectNavItemClass("/banners")}>
                      <Link
                        to="/banners"
                        className={getDirectNavLinkClass("/banners")}
                      >
                        <i className="bx bx bx-photo-album me-2"></i>
                        {props.t("Banners")}{" "}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isOfferBannerView ? (
                    <li className={getDirectNavItemClass("/offer-banners")}>
                      <Link
                        to="/offer-banners"
                        className={getDirectNavLinkClass("/offer-banners")}
                      >
                        <i className="bx bx-purchase-tag me-2"></i>
                        {props.t("Offer Banners")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isMenusView ? (
                    <li className={getDropdownNavItemClass("menus")}>
                      <Link
                        to="#"
                        className={getDropdownTriggerClass("menus")}
                        data-horizontal-dropdown="menus"
                        onClick={e => toggleDropdown("menus", e)}
                      >
                        <i className="bx bx-food-menu me-2"></i>
                        {props.t("Menus")} <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="menus">
                        {(userSideBarPermissions?.MenusSubPermissions
                          ?.menu_full_access_view?.isEnabled) && (
                          <Link to="/menus/menu-setup" className="dropdown-item">
                            {props.t("Menu Setup")}
                          </Link>
                        )}
                        {(userSideBarPermissions?.MenusSubPermissions
                          ?.menu_full_access_view?.isEnabled) && (
                          <Link to="/menus/categories" className="dropdown-item">
                            {props.t("Categories")}
                          </Link>
                        )}
                        {(userSideBarPermissions?.MenusSubPermissions
                          ?.item_access_view?.isEnabled ||
                          userSideBarPermissions?.MenusSubPermissions
                            ?.menu_full_access_view?.isEnabled) && (
                          <Link to="/menus/items" className="dropdown-item">
                            {props.t("Items")}
                          </Link>
                        )}
                        {userSideBarPermissions.isItemTagView && (
                          <Link to="/menus/item-tags" className="dropdown-item">
                            {props.t("Item Tags")}
                          </Link>
                        )}
                        {userSideBarPermissions?.MenusSubPermissions
                          ?.modifier_access_view?.isEnabled && (
                          <Link
                            to="/menus/modifier-groups"
                            className="dropdown-item"
                          >
                            {props.t("Modifier Groups")}
                          </Link>
                        )}
                        {userSideBarPermissions?.MenusSubPermissions
                          ?.modifier_access_view?.isEnabled && (
                          <Link to="/menus/modifiers" className="dropdown-item">
                            {props.t("Modifiers")}
                          </Link>
                        )}
                        {userSideBarPermissions?.MenusSubPermissions
                          ?.combo_access_view?.isEnabled && (
                          <>
                            <Link
                              to="/menus/item-groups"
                              className="dropdown-item"
                            >
                              {props.t("Item Groups")}
                            </Link>
                            <Link to="/menus/combos" className="dropdown-item">
                              {props.t("Combos")}
                            </Link>
                          </>
                        )}                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isRolesView ? (
                    <li className={getDirectNavItemClass("/roles")}>
                      <Link
                        className={getDirectNavLinkClass("/roles")}
                        to="/roles"
                      >
                        <i className="bx bx-briefcase me-2"></i>
                        {props.t("Roles Management")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isLocationsView ? (
                    <li className={getDirectNavItemClass("/branches")}>
                      <Link
                        className={getDirectNavLinkClass("/branches")}
                        // onClick={e => {
                        //   e.preventDefault()
                        //   setdashboard(!dashboard)
                        // }}
                        to="/branches"
                      >
                        <i className="bx bx-target-lock me-2"></i>
                        {props.t("Branches")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isUsersView ? (
                    <li className={getDirectNavItemClass("/users")}>
                      <Link
                        className={getDirectNavLinkClass("/users")}
                        to="/users"
                      >
                        <i className="bx bx-user me-2"></i>
                        {props.t("User Management")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions?.isUserActivityView ? (
                    <li className={getDirectNavItemClass("/user-activity")}>
                      <Link
                        className={getDirectNavLinkClass("/user-activity")}
                        to="/user-activity"
                      >
                        <i className="bx bx-list-check me-2"></i>
                        {props.t("User Activity")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isCustomersView ? (
                    <li className={getDropdownNavItemClass("customers")}>
                      <Link
                        className={getDropdownTriggerClass("customers")}
                        to="#"
                        data-horizontal-dropdown="customers"
                        onClick={e => toggleDropdown("customers", e)}
                      >
                        <i className="bx bx-group me-2"></i>
                        {props.t("Customers")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="customers">
                        <Link to="/customers" className="dropdown-item">
                          {props.t("Customers")}
                        </Link>
                        {userSideBarPermissions?.customerSubPermissions
                          ?.view_loyalty_points?.isEnabled && (
                          <Link to="/customers/loyalty" className="dropdown-item">
                            {props.t("Loyalty")}
                          </Link>
                        )}                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isCustomerSupportView ? (
                    <li className={getDropdownNavItemClass("customer-support")}>
                      <Link
                        className={getDropdownTriggerClass("customer-support")}
                        to="#"
                        data-horizontal-dropdown="customer-support"
                        onClick={e => toggleDropdown("customer-support", e)}
                      >
                        <i className="bx bx-group me-2"></i>
                        {props.t("Customer Support")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="customer-support">
                        <Link to="/support-issues" className="dropdown-item">
                          {props.t("Support Issues")}
                        </Link>
                        <Link to="/support-tickets" className="dropdown-item">
                          {props.t("Support Tickets")}
                        </Link>                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isCountriesView ? (
                    <li className={getDropdownNavItemClass("countries")}>
                      <Link
                        className={getDropdownTriggerClass("countries")}
                        to="#"
                        data-horizontal-dropdown="countries"
                        onClick={e => toggleDropdown("countries", e)}
                      >
                        <i className="bx bxs-flag-alt me-2"></i>
                        {props.t("Countries")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="countries">
                        <Link to="/countries" className="dropdown-item">
                          {props.t("Countries")}
                        </Link>
                        {userSideBarPermissions.isRegionView && (
                          <Link to="/regions" className="dropdown-item">
                            {props.t("Regions")}
                          </Link>
                        )}
                        {userSideBarPermissions.isCityView && (
                          <Link to="/cities" className="dropdown-item">
                            {props.t("Cities")}
                          </Link>
                        )}
                        {userSideBarPermissions.isZoneView && (
                          <Link to="/zones" className="dropdown-item">
                            {props.t("Zones")}
                          </Link>
                        )}                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isReportView ? (
                    <li className={getDropdownNavItemClass("reports")}>
                      <Link
                        className={getDropdownTriggerClass("reports")}
                        to="#"
                        data-horizontal-dropdown="reports"
                        onClick={e => toggleDropdown("reports", e)}
                      >
                        <i className="bx bx-bar-chart me-2"></i>
                        {props.t("Reports")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="reports">
                        <Link to="/sales-reports" className="dropdown-item">
                          {props.t("Sales Reports")}
                        </Link>
                        <Link to="/wallet-reports" className="dropdown-item">
                          {props.t("Wallet Reports")}
                        </Link>
                        <Link to="/payments-reports" className="dropdown-item">
                          {props.t("Payments Reports")}
                        </Link>
                        <Link to="/business-tax-reports" className="dropdown-item">
                          {props.t("Business Tax Reports")}
                        </Link>
                        <Link to="/loyalty-points-reports" className="dropdown-item">
                          {props.t("Loyalty Points Reports")}
                        </Link>                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isOrdersView ? (
                    ordersUseDropdown ? (
                      <li className={getDropdownNavItemClass("orders")}>
                        <Link
                          className={getDropdownTriggerClass("orders")}
                          to="#"
                          data-horizontal-dropdown="orders"
                          onClick={e => toggleDropdown("orders", e)}
                        >
                          <i className="bx bx-bar-chart me-2"></i>
                          {props.t("Orders")}
                          <div className="arrow-down"></div>
                        </Link>
                        <HorizontalDropdownMenu id="orders">
                          {visibleOrderBrands.map(brand => (
                            <Link
                              key={brand.brandId}
                              to={getBrandOrdersBasePath(brand.brandId)}
                              className="dropdown-item"
                            >
                              {brand.displayName || brand.brandName}
                            </Link>
                          ))}
                        </HorizontalDropdownMenu>
                      </li>
                    ) : (
                      <li className={getDirectNavItemClass(ordersTargetPath)}>
                        <Link
                          className={getDirectNavLinkClass(ordersTargetPath)}
                          to={ordersTargetPath}
                        >
                          <i className="bx bx-bar-chart me-2"></i>
                          {props.t("Orders")}
                        </Link>
                      </li>
                    )
                  ) : null}

                  {userSideBarPermissions.isBookingView ? (
                    <li className={getDirectNavItemClass("/bookings")}>
                      <Link
                        to="/bookings"
                        className={getDirectNavLinkClass("/bookings")}
                      >
                        <i className="bx bx-calendar me-2"></i>
                        <span>{props.t("Bookings")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isQrCodeView ? (
                    <li className={getDirectNavItemClass("/generate-qr-code")}>
                      <Link
                        to="/generate-qr-code"
                        className={getDirectNavLinkClass("/generate-qr-code")}
                      >
                        <i className="bx bxs-barcode me-2"></i>
                        <span>{props.t("Generate QR Code")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isApiLogsView ? (
                    <li className={getDirectNavItemClass("/api-logs")}>
                      <Link
                        to="/api-logs"
                        className={getDirectNavLinkClass("/api-logs")}
                      >
                        <i className="bx bx-menu me-2"></i>
                        <span>{props.t("API Logs")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isRatingView ? (
                    <li className={getDropdownNavItemClass("ratings")}>
                      <Link
                        className={getDropdownTriggerClass("ratings")}
                        to="#"
                        data-horizontal-dropdown="ratings"
                        onClick={e => toggleDropdown("ratings", e)}
                      >
                        <i className="bx bx-star me-2"></i>
                        {props.t("Ratings")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="ratings">
                        <Link to="/rating-questions" className="dropdown-item">
                          {props.t("Rating Questions")}
                        </Link>
                        <Link to="/branch-ratings" className="dropdown-item">
                          {props.t("Branch Ratings")}
                        </Link>                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isFaqView ? (
                    <li className={getDirectNavItemClass("/faq")}>
                      <Link
                        to="/faq"
                        className={getDirectNavLinkClass("/faq")}
                      >
                        <i className="bx bx-help-circle me-2"></i>
                        <span>{props.t("FAQ")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isVehicleView ? (
                    <li className={getDirectNavItemClass("/vehicles")}>
                      <Link
                        to="/vehicles"
                        className={getDirectNavLinkClass("/vehicles")}
                      >
                        <i className="bx bxs-car me-2"></i>
                        <span>{props.t("Vehicles")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isBrandsManagementView ? (
                    <li className={getDirectNavItemClass("/brands")}>
                      <Link
                        to="/brands"
                        className={getDirectNavLinkClass("/brands")}
                      >
                        <i className="bx bx-store me-2"></i>
                        {props.t("Brands")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isDeliveryPartnersView ? (
                    <li className={getDirectNavItemClass("/delivery-partners")}>
                      <Link
                        to="/delivery-partners"
                        className={getDirectNavLinkClass("/delivery-partners")}
                      >
                        <i className="bx bx-cycling me-2"></i>
                        {props.t("Deliver Management")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isTermsConditionsView ? (
                    <li className={getDirectNavItemClass("/terms-and-conditions")}>
                      <Link
                        to="/terms-and-conditions"
                        className={getDirectNavLinkClass("/terms-and-conditions")}
                      >
                        <i className="bx bxs-file me-2"></i>
                        <span>{props.t("Terms And Conditions")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isPrivacyPolicyView ? (
                    <li className={getDirectNavItemClass("/privacy-policy")}>
                      <Link
                        to="/privacy-policy"
                        className={getDirectNavLinkClass("/privacy-policy")}
                      >
                        <i className="bx bxs-lock me-2"></i>
                        <span>{props.t("Privacy Policy")}</span>
                      </Link>
                    </li>
                  ) : null}

                  {/* {userSideBarPermissions.isGiftCardView ? (
                    <li className="nav-item dropdown">
                      <Link
                        to="/eidiyah"
                        className="nav-link dropdown-toggle arrow-none"
                      >
                        <i className="bx bxs-gift me-2"></i>
                        <span>{props.t("Eidiyah")}</span>
                      </Link>
                    </li>
                  ) : null} */}

                  {userSideBarPermissions.isGiftCardView ||
                  userSideBarPermissions.isDynamicLinkView ||
                  userSideBarPermissions.isGiftCardUsageView ? (
                    <li className={getDropdownNavItemClass("gift-card")}>
                      <Link
                        className={getDropdownTriggerClass("gift-card")}
                        to="#"
                        data-horizontal-dropdown="gift-card"
                        onClick={e => toggleDropdown("gift-card", e)}
                      >
                        <i className="bx bx-trophy me-2"></i>
                        {props.t("Gift Card")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="gift-card">
                        {userSideBarPermissions.isGiftCardView && (
                          <Link to="/eidiyah" className="dropdown-item">
                            {props.t("Gift Card Link")}
                          </Link>
                        )}
                        {userSideBarPermissions.isDynamicLinkView && (
                          <Link to="/dynamic-link" className="dropdown-item">
                            {props.t("Gift Card Deep Link")}
                          </Link>
                        )}
                        {userSideBarPermissions.isGiftCardUsageView && (
                          <Link to="/eidiyah-usage" className="dropdown-item">
                            {props.t("Gift Card Usage")}
                          </Link>
                        )}                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {(userSideBarPermissions.isPromotionView ||
                    userSideBarPermissions.isCouponRedeemView) ? (
                    <li className={getDropdownNavItemClass("discounts")}>
                      <Link
                        className={getDropdownTriggerClass("discounts")}
                        to="#"
                        data-horizontal-dropdown="discounts"
                        onClick={e => toggleDropdown("discounts", e)}
                      >
                        <i className="bx bx-trophy me-2"></i>
                        {props.t("Discounts & Coupons")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="discounts">
                        {userSideBarPermissions.isPromotionView && (
                          <>
                            <Link to="/discounts" className="dropdown-item">
                              {props.t("Discounts")}
                            </Link>
                            <Link to="/coupons-group" className="dropdown-item">
                              {props.t("Coupons Group")}
                            </Link>
                            <Link to="/coupons" className="dropdown-item">
                              {props.t("Coupons")}
                            </Link>
                            <Link to="/coupon-usage" className="dropdown-item">
                              {props.t("Coupon Usage")}
                            </Link>
                            <Link
                              to="/generate-bulk-coupons"
                              className="dropdown-item"
                            >
                              {props.t("Generate Bulk Coupon")}
                            </Link>
                          </>
                        )}
                        {userSideBarPermissions.isCouponRedeemView && (
                          <Link to="/wallet-coupons" className="dropdown-item">
                            {props.t("Wallet Coupons")}
                          </Link>
                        )}                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isNotificationView ? (
                    <li className={getDropdownNavItemClass("notifications")}>
                      <Link
                        className={getDropdownTriggerClass("notifications")}
                        to="#"
                        data-horizontal-dropdown="notifications"
                        onClick={e => toggleDropdown("notifications", e)}
                      >
                        <i className="bx  bx-bell me-2"></i>
                        {props.t("Notifications")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="notifications">
                        <Link to="/app-notifications" className="dropdown-item">
                          {props.t("App Notifications")}
                        </Link>
                        <Link to="/push-notifications" className="dropdown-item">
                          {props.t("Push Notifications")}
                        </Link>                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {/* {userSideBarPermissions.isAppSettingsView ? (
                    <li className="nav-item dropdown">
                      <Link
                        to="/app-settings"
                        className="nav-link dropdown-toggle arrow-none"
                      >
                        <i className="bx bxs-cog me-2"></i>
                        <span>{props.t("App Settings")}</span>
                      </Link>
                    </li>
                  ) : null} */}

                  {userSideBarPermissions.isReferralView ||
                  userSideBarPermissions.isAddEmailView ||
                  userSideBarPermissions.isAppSettingsView ||
                  userSideBarPermissions.isLoyaltyView ||
                  userSideBarPermissions.isIngredientView ||
                  userSideBarPermissions.isAllergenView ||
                  userSideBarPermissions?.MenusUpdateSubPermissions
                    ?.clear_menu_cache?.isEnabled ||
                  userSideBarPermissions?.AppSettingsViewSubPermissions
                    ?.enable_erp_integration?.isEnabled ||
                  userSideBarPermissions?.AppSettingsUpdateSubPermissions
                    ?.enable_erp_integration?.isEnabled ||
                  userSideBarPermissions?.isEidiyahSettingsView ||
                  userSideBarPermissions.isOrderCancelSettingsView ||
                  userSideBarPermissions.isDeliveryPartnerSettingsView ||
                  userSideBarPermissions.isStaticMaintenanceView ||
                  isSocialAuthSidebarVisible ||
                  accountType !== "superadmin" ? (
                    <li className={getDropdownNavItemClass("settings")}>
                      <Link
                        className={getDropdownTriggerClass("settings")}
                        to="#"
                        data-horizontal-dropdown="settings"
                        onClick={e => toggleDropdown("settings", e)}
                      >
                        <i className="bx  bx-cog me-2"></i>
                        {props.t("Settings")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="settings">
                        {accountType !== "superadmin" && (
                          <Link to="/my-preference" className="dropdown-item">
                            {props.t("My Preference")}
                          </Link>
                        )}
                        {(userSideBarPermissions.isAppSettingsView ||
                          userSideBarPermissions?.MenusUpdateSubPermissions
                            ?.clear_menu_cache?.isEnabled ||
                          userSideBarPermissions?.AppSettingsViewSubPermissions
                            ?.enable_erp_integration?.isEnabled ||
                          userSideBarPermissions?.AppSettingsUpdateSubPermissions
                            ?.enable_erp_integration?.isEnabled) && (
                          <Link to="/app-settings" className="dropdown-item">
                            {props.t("App Settings")}
                          </Link>
                        )}
                        {userSideBarPermissions?.isEidiyahSettingsView && (
                          <Link to="/eidiyah-settings" className="dropdown-item">
                            {props.t("Gift Card Settings")}
                          </Link>
                        )}
                        {userSideBarPermissions.isAddEmailView && (
                          <Link to="/add-emails" className="dropdown-item">
                            {props.t("Add Emails")}
                          </Link>
                        )}
                        {userSideBarPermissions.isReferralView && (
                          <Link to="/referral-settings" className="dropdown-item">
                            {props.t("Referral Settings")}
                          </Link>
                        )}
                        {userSideBarPermissions.isLoyaltyView && (
                          <Link to="/loyalty-settings" className="dropdown-item">
                            {props.t("Loyalty Settings")}
                          </Link>
                        )}
                        {userSideBarPermissions.isOrderCancelSettingsView && (
                          <Link
                            to="/order-cancel-settings"
                            className="dropdown-item"
                          >
                            {props.t("Order Cancel Settings")}
                          </Link>
                        )}
                        {userSideBarPermissions.isDeliveryPartnerSettingsView && (
                          <Link
                            to="/delivery-partner-settings"
                            className="dropdown-item"
                          >
                            {props.t("Delivery Partner Settings")}
                          </Link>
                        )}
                        {userSideBarPermissions.isIngredientView && (
                          <Link to="/ingredients" className="dropdown-item">
                            {props.t("Ingredients")}
                          </Link>
                        )}
                        {userSideBarPermissions.isAllergenView && (
                          <Link to="/allergens" className="dropdown-item">
                            {props.t("Allergens")}
                          </Link>
                        )}
                        {userSideBarPermissions.isStaticMaintenanceView && (
                          <Link to="/static-maintenance" className="dropdown-item">
                            {props.t("Static Maintenance")}
                          </Link>
                        )}
                        {isSocialAuthSidebarVisible && (
                          <Link to="/settings/social-auth" className="dropdown-item">
                            {props.t("Social Login")}
                          </Link>
                        )}
                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isBillingView ? (
                    <li className={getDirectNavItemClass("/billing")}>
                      <Link
                        to="/billing"
                        className={getDirectNavLinkClass("/billing")}
                      >
                        <i className="bx bx-credit-card me-2"></i>
                        {props.t("Billing")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isIntegrationsView ? (
                    <li className={getDirectNavItemClass("/integrations")}>
                      <Link
                        to="/integrations"
                        className={getDirectNavLinkClass("/integrations")}
                      >
                        <i className="bx bx-plug me-2"></i>
                        {props.t("Integrations")}
                      </Link>
                    </li>
                  ) : null}

                  {userSideBarPermissions.isAPIKeyView ||
                  userSideBarPermissions.isBranchSyncView ||
                  userSideBarPermissions.isMenuSyncView ? (
                    <li className={getDropdownNavItemClass("pos-settings")}>
                      <Link
                        className={getDropdownTriggerClass("pos-settings")}
                        to="#"
                        data-horizontal-dropdown="pos-settings"
                        onClick={e => toggleDropdown("pos-settings", e)}
                      >
                        <i className="bx bx-slider me-2"></i>
                        {props.t("POS Settings")}
                        <div className="arrow-down"></div>
                      </Link>
                      <HorizontalDropdownMenu id="pos-settings">
                        {userSideBarPermissions.isAPIKeyView && (
                          <Link to="/api-key" className="dropdown-item">
                            {props.t("API key")}
                          </Link>
                        )}
                        {userSideBarPermissions.isBranchSyncView && (
                          <Link to="/branch-sync" className="dropdown-item">
                            {props.t("Branch Sync")}
                          </Link>
                        )}
                        {userSideBarPermissions.isMenuSyncView && (
                          <Link to="/menu-sync" className="dropdown-item">
                            {props.t("Menu Sync")}
                          </Link>
                        )}                      </HorizontalDropdownMenu>
                    </li>
                  ) : null}
                    </>
                </ul>
              </div>
            </Collapse>
            <div
              ref={setDropdownPortalEl}
              className="horizontal-dropdown-portal"
            />
            </HorizontalNavDropdownContext.Provider>
          </nav>
        </div>
      </div>
    </React.Fragment>
  )
}

Navbar.propTypes = {
  leftMenu: PropTypes.any,
  location: PropTypes.any,
  menuOpen: PropTypes.any,
  t: PropTypes.any,
}

const mapStatetoProps = state => {
  const { leftMenu } = state.Layout
  return { leftMenu }
}

export default withRouter(
  connect(mapStatetoProps, {})(withTranslation()(Navbar))
)
